import { useState, useEffect, useRef } from "react";
import { AIDraftsBanner } from "@/components/AIDraftsBanner";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import axios from "axios";
import { 
  MessageCircle, Send, ArrowLeft, Phone, User, Clock, 
  CheckCheck, Check, AlertCircle, Search, Plus, X, Edit2, Trash2,
  Image, Paperclip, Loader2, FileText, Calendar, DollarSign, Zap, BookOpen
} from "lucide-react";
import { toast } from "sonner";
import BottomNav from "@/components/BottomNav";
import Header from "@/components/Header";
import { getSessionToken } from "@/utils/auth";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import JobDialog from "@/components/JobDialog";
import InlineEstimatePanel from "@/components/messages/InlineEstimatePanel";
import ImageLightbox from "@/components/messages/ImageLightbox";
import TemplateInputModal from "@/components/messages/TemplateInputModal";
import { ReviewReminderBanner } from "@/components/ReviewReminderBanner";
import { ReplyDraftCard } from "@/components/messages/ReplyDraftCard";

const BACKEND_URL = window.APP_CONFIG?.BACKEND_URL || process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

export default function Messages() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [conversations, setConversations] = useState([]);
  // True once `fetchConversations` has resolved at least once. The deep-link
  // effect waits on this because .find() on an empty array during the first
  // render was misclassifying every "no match yet" as "no conversation
  // exists", which clobbered the deep-link intent and dropped the user on
  // the New Message picker even though the thread exists.
  const [conversationsLoaded, setConversationsLoaded] = useState(false);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [showNewMessageDialog, setShowNewMessageDialog] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [customerSearch, setCustomerSearch] = useState("");
  const [showEditCustomerDialog, setShowEditCustomerDialog] = useState(false);
  const [editingCustomerName, setEditingCustomerName] = useState("");
  const [savingCustomer, setSavingCustomer] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [conversationToDelete, setConversationToDelete] = useState(null);
  const [pendingCustomerSelect, setPendingCustomerSelect] = useState(null);
  const [attachedImages, setAttachedImages] = useState([]);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [showTemplateMenu, setShowTemplateMenu] = useState(false);
  const [customerEstimate, setCustomerEstimate] = useState(null);
  const [showJobDialog, setShowJobDialog] = useState(false);
  const [lightboxImage, setLightboxImage] = useState(null);
  const [statusFilter, setStatusFilter] = useState("nurturing");
  const [showEstimatePanel, setShowEstimatePanel] = useState(false);
  const [editingLineItems, setEditingLineItems] = useState([]);
  const [savingEstimate, setSavingEstimate] = useState(false);
  const [sentQuotes, setSentQuotes] = useState([]);
  const [loadingQuotes, setLoadingQuotes] = useState(false);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const templateMenuRef = useRef(null);

  // Message templates (loaded from DB via /api/quick-replies — admin manages these in Settings → Quick Reply Templates)
  const [dbTemplates, setDbTemplates] = useState([]);
  const [guides, setGuides] = useState([]);
  const pendingTextRef = useRef(null);

  // State for template input modal
  const [templateInputOpen, setTemplateInputOpen] = useState(false);
  const [pendingTemplate, setPendingTemplate] = useState(null);
  const [templateInputValue, setTemplateInputValue] = useState("");

  useEffect(() => {
    fetchUser();
    // Load the admin's quick-reply templates (auto-seeds defaults on first call)
    const token = getSessionToken();
    axios.get(`${API}/quick-replies?enabled_only=true`, {
      headers: { Authorization: `Bearer ${token}` },
    }).then((res) => setDbTemplates(res.data || []))
      .catch(() => { /* non-fatal — picker just shows empty */ });
    axios.get(`${API}/advertorials`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => setGuides((res.data || []).filter((a) => a.kind === "guide" && a.status === "published")))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (user) {
      fetchConversations();
      // Poll for new messages every 15 seconds
      const interval = setInterval(fetchConversations, 15000);
      return () => clearInterval(interval);
    }
  }, [user]);

  // Handle URL parameters and state for deep linking from Estimates/Jobs.
  // Gated on `conversationsLoaded` so .find() on an empty array during
  // the first render doesn't misclassify "not loaded yet" as "no match →
  // open New Message picker".
  useEffect(() => {
    if (!user || !conversationsLoaded) return;
    
    const stateCustomerId = location.state?.customerId;
    const prefill = searchParams.get('text');
    if (prefill) pendingTextRef.current = prefill;
    const customerId = stateCustomerId || searchParams.get('customer');
    const phone = searchParams.get('phone');
    const name = searchParams.get('name');
    
    // For customer_id deep links, we can proceed once conversations have loaded (even if empty)
    // For phone-based lookups, we need conversations to search through
    if (customerId) {
        // Find conversation by customer_id
        const conv = conversations.find(c => c.customer_id === customerId);
        if (conv) {
          setSelectedConversation(conv);
          // Clear params and state
          setSearchParams({});
          // Clear location state
          window.history.replaceState({}, document.title);
        } else if (phone) {
          // Stale customer_id (orphaned / dedup rewrite) — try the phone
          // fallback before firing the customers fetch. This is the
          // scenario Brett hits from the Open Estimates Map when the
          // estimate's stored customer_id no longer matches the customer
          // that actually owns the conversation.
          const normalizedPhone = phone.replace(/\D/g, '').slice(-10);
          const byPhone = conversations.find(c => {
            const convPhone = (c.phone || '').replace(/\D/g, '').slice(-10);
            return convPhone === normalizedPhone;
          });
          if (byPhone) {
            setSelectedConversation(byPhone);
            setSearchParams({});
            window.history.replaceState({}, document.title);
            return;
          }
          // No conversation for that phone yet — start one with the customer directly.
          startConversationForCustomerId(customerId, phone);
        } else {
          startConversationForCustomerId(customerId, null);
        }
      } else if (phone && conversations.length > 0) {
        // Find conversation by phone number
        const normalizedPhone = phone.replace(/\D/g, '').slice(-10);
        const conv = conversations.find(c => {
          const convPhone = (c.phone || '').replace(/\D/g, '').slice(-10);
          return convPhone === normalizedPhone;
        });
        if (conv) {
          setSelectedConversation(conv);
          setSearchParams({});
        } else {
          // Start new message dialog with pre-filled info
          setCustomerSearch(name || phone);
          setShowNewMessageDialog(true);
          fetchCustomers();
          setSearchParams({});
        }
      }
  }, [user, conversations, searchParams, location.state, conversationsLoaded]);

  // Deep link with no existing thread: look the customer up (by id, then phone) and start one.
  const startConversationForCustomerId = (customerId, phone) => {
    const last10 = (p) => (p || "").replace(/\D/g, "").slice(-10);
    axios.get(`${API}/customers`, { headers: { Authorization: `Bearer ${getSessionToken()}` } })
      .then((response) => {
        const list = response.data || [];
        const customer = list.find((c) => c.customer_id === customerId)
          || (phone ? list.find((c) => last10(c.phoneNumber) === last10(phone)) : null);
        if (customer) {
          startNewConversation(customer);
        } else {
          if (phone) setCustomerSearch(phone);
          setShowNewMessageDialog(true);
          fetchCustomers();
        }
      })
      .catch(() => {
        setShowNewMessageDialog(true);
        fetchCustomers();
      });
    setSearchParams({});
    window.history.replaceState({}, document.title);
  };

  useEffect(() => {
    if (selectedConversation) {
      fetchMessages(selectedConversation.customer_id);
      markAsRead(selectedConversation.customer_id);
      if (pendingTextRef.current) {
        setNewMessage(pendingTextRef.current);
        pendingTextRef.current = null;
      }
    }
  }, [selectedConversation]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const fetchUser = async () => {
    try {
      const token = getSessionToken();
      if (!token) {
        navigate("/login");
        return;
      }
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.role !== "Admin") {
        toast.error("Access denied - Admin only");
        navigate("/jobs");
        return;
      }
      setUser(response.data);
    } catch (error) {
      navigate("/login");
    } finally {
      setLoading(false);
    }
  };

  const fetchConversations = async () => {
    try {
      const token = getSessionToken();
      const response = await axios.get(`${API}/messages/conversations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConversations(response.data);
    } catch (error) {
      console.error("Failed to fetch conversations:", error);
    } finally {
      // Mark loaded even on error so the deep-link effect doesn't hang
      // forever if the first fetch fails transiently.
      setConversationsLoaded(true);
    }
  };

  const fetchSentQuotes = async () => {
    setLoadingQuotes(true);
    try {
      const token = getSessionToken();
      const response = await axios.get(`${API}/messages/sent-quotes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSentQuotes(response.data);
    } catch (error) {
      console.error("Failed to fetch sent quotes:", error);
    } finally {
      setLoadingQuotes(false);
    }
  };

  // Fetch customer's estimate for booking link
  const fetchCustomerEstimate = async (customerId) => {
    if (!customerId) return;
    try {
      const token = getSessionToken();
      const response = await axios.get(`${API}/customers/${customerId}/estimate`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCustomerEstimate(response.data);
    } catch (error) {
      // Customer may not have an estimate
      setCustomerEstimate(null);
    }
  };

  // Fetch estimate when conversation changes
  useEffect(() => {
    if (selectedConversation?.customer_id) {
      fetchCustomerEstimate(selectedConversation.customer_id);
    } else {
      setCustomerEstimate(null);
    }
    // Reset estimate panel when conversation changes
    setShowEstimatePanel(false);
    setEditingLineItems([]);
  }, [selectedConversation?.customer_id]);

  // Initialize editing line items when estimate panel opens
  useEffect(() => {
    if (showEstimatePanel && customerEstimate?.estimate_data?.line_items) {
      setEditingLineItems([...customerEstimate.estimate_data.line_items]);
    }
  }, [showEstimatePanel, customerEstimate]);

  // Save estimate changes
  const saveEstimateChanges = async () => {
    if (!customerEstimate?.lead_id) return;
    
    setSavingEstimate(true);
    try {
      const token = getSessionToken();
      await axios.put(
        `${API}/leads/${customerEstimate.lead_id}/estimate`,
        { line_items: editingLineItems },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Estimate updated");
      // Refresh estimate data
      fetchCustomerEstimate(selectedConversation.customer_id);
      setShowEstimatePanel(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update estimate");
    } finally {
      setSavingEstimate(false);
    }
  };

  // Update line item quantity
  const updateLineItemQty = (index, delta) => {
    const updated = [...editingLineItems];
    const newQty = Math.max(1, (updated[index].quantity || 1) + delta);
    updated[index] = { ...updated[index], quantity: newQty };
    setEditingLineItems(updated);
  };

  // Remove line item
  const removeLineItem = (index) => {
    setEditingLineItems(editingLineItems.filter((_, i) => i !== index));
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
  };

  // Close template menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (templateMenuRef.current && !templateMenuRef.current.contains(e.target)) {
        setShowTemplateMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchCustomers = async () => {
    try {
      const token = getSessionToken();
      const response = await axios.get(`${API}/customers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCustomers(response.data);
      
      // If we have a pending customer to select (from URL params), auto-select them
      if (pendingCustomerSelect) {
        const customer = response.data.find(c => c.customer_id === pendingCustomerSelect);
        if (customer) {
          startNewConversation(customer);
        }
        setPendingCustomerSelect(null);
      }
    } catch (error) {
      console.error("Failed to fetch customers:", error);
    }
  };

  const startNewConversation = (customer) => {
    setSelectedConversation({
      customer_id: customer.customer_id,
      customer_name: customer.fullName,
      phone: customer.phoneNumber
    });
    setShowNewMessageDialog(false);
    setCustomerSearch("");
  };

  const fetchMessages = async (customerId) => {
    try {
      const token = getSessionToken();
      const response = await axios.get(`${API}/messages/${customerId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessages(response.data);
    } catch (error) {
      console.error("Failed to fetch messages:", error);
      toast.error("Failed to load messages");
    }
  };

  const markAsRead = async (customerId) => {
    try {
      const token = getSessionToken();
      await axios.post(`${API}/messages/${customerId}/read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (error) {
      // Silently fail
    }
  };

  const isUnknownCustomer = (name) => {
    return !name || name.startsWith("Unknown (") || name === "Unknown";
  };

  const openEditCustomerDialog = () => {
    setEditingCustomerName(isUnknownCustomer(selectedConversation.customer_name) ? "" : selectedConversation.customer_name);
    setShowEditCustomerDialog(true);
  };

  const handleSaveCustomerName = async () => {
    if (!editingCustomerName.trim()) {
      toast.error("Please enter a name");
      return;
    }

    setSavingCustomer(true);
    try {
      const token = getSessionToken();
      
      // Update customer name
      await axios.put(
        `${API}/customers/${selectedConversation.customer_id}`,
        { fullName: editingCustomerName.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Update local state
      setSelectedConversation({
        ...selectedConversation,
        customer_name: editingCustomerName.trim()
      });

      // Update conversations list
      setConversations(conversations.map(conv => 
        conv.customer_id === selectedConversation.customer_id
          ? { ...conv, customer_name: editingCustomerName.trim() }
          : conv
      ));

      toast.success("Customer name updated!");
      setShowEditCustomerDialog(false);
    } catch (error) {
      toast.error("Failed to update customer name");
    } finally {
      setSavingCustomer(false);
    }
  };

  // Archive/Unarchive a conversation
  const toggleArchive = async (conversation, e) => {
    e?.stopPropagation();
    const identifier = conversation.customer_id || conversation.phone;
    const isArchived = conversation.archived;
    
    try {
      const token = getSessionToken();
      if (isArchived) {
        await axios.delete(`${API}/messages/archive/${encodeURIComponent(identifier)}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Conversation unarchived");
      } else {
        await axios.post(`${API}/messages/archive/${encodeURIComponent(identifier)}`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Conversation archived");
      }
      fetchConversations();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update archive status");
    }
  };

  // Filter conversations by status
  const filteredConversations = conversations.filter(conv => {
    const matchesSearch = !searchQuery || 
      conv.customer_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      conv.phone?.includes(searchQuery) ||
      conv.last_message?.toLowerCase().includes(searchQuery.toLowerCase());
    
    if (!matchesSearch) return false;
    
    // Handle "techs" filter - show only tech conversations
    if (statusFilter === "techs") {
      return conv.is_tech_conversation || conv.customer_id?.startsWith("tech_");
    }
    
    // Exclude tech conversations from other filters
    if (conv.is_tech_conversation || conv.customer_id?.startsWith("tech_")) {
      return false;
    }
    
    return conv.conversation_status === statusFilter;
  });

  // Get counts for each filter tab
  const getStatusCounts = () => {
    const counts = { nurturing: 0, scheduled: 0, converted: 0, lost: 0, quotes: sentQuotes.length, techs: 0 };
    conversations.forEach(conv => {
      // Count tech conversations separately
      if (conv.is_tech_conversation || conv.customer_id?.startsWith("tech_")) {
        counts.techs++;
        return;
      }
      
      const s = conv.conversation_status;
      if (s === "scheduled") counts.scheduled++;
      else if (s === "converted") counts.converted++;
      else if (s === "lost") counts.lost++;
      else counts.nurturing++;
    });
    return counts;
  };

  // Apply a quick-reply template loaded from /api/quick-replies.
  // Schema: { template_id, label, body, enabled, sort_order }
  // Supported merge tags in `body`:
  //   {first_name} / {firstName}  → customer's first name (falls back to "there")
  //   {name}                       → full customer name
  //   {booking_link}               → instant-quote resume link if estimate exists
  const applyTemplate = (template) => {
    const fullName = selectedConversation?.customer_name || "";
    const firstName = fullName.split(" ")[0] || "there";
    const bookingLink = customerEstimate?.lead_id
      ? `https://jobs.screenzz.com/instant-estimate?resume=${customerEstimate.lead_id}`
      : "";

    let message = (template.body || "")
      .replace(/\{first_name\}/g, firstName)
      .replace(/\{firstName\}/g, firstName)
      .replace(/\{name\}/g, fullName || firstName)
      .replace(/\{booking_link\}/g, bookingLink)
      .replace(/\{bookingLink\}/g, bookingLink);

    if (!bookingLink && /\{(booking_link|bookingLink)\}/.test(template.body || "")) {
      // Body referenced the link but we don't have one — warn & strip
      // any stray "here: " phrasing so we don't ship a dangling colon.
      toast.warning("No estimate found for this customer — booking link not included");
      message = message.replace(/ here: ?$/m, "").replace(/  +/g, " ");
    }

    setNewMessage(message);
    setShowTemplateMenu(false);
  };

  // Drop a How-To Guide link into the composer; `c` lets the guide's form attach replies to this thread.
  const insertGuideLink = (guide) => {
    const origin = window.location.hostname.includes("preview") ? window.location.origin : "https://jobs.screenzz.com";
    const cid = selectedConversation?.customer_id || "";
    const link = `${origin}/learn/${guide.slug}${cid && !cid.startsWith("tech_") ? `?c=${cid}` : ""}`;
    const firstName = (selectedConversation?.customer_name || "").split(" ")[0];
    const intro = `${firstName ? `Hi ${firstName}! ` : ""}Here's a quick guide: ${guide.title}\n${link}`;
    setNewMessage((m) => (m.trim() ? `${m.trimEnd()}\n${link}` : intro));
    setShowTemplateMenu(false);
  };

  // Legacy alias kept in case anything else still calls it.
  const applyTemplateWithValues = (template) => applyTemplate(template);

  // Handle template input submission
  const handleTemplateInputSubmit = () => {
    if (!pendingTemplate || !templateInputValue.trim()) {
      toast.error("Please enter a value");
      return;
    }
    
    applyTemplateWithValues(pendingTemplate, { eta: templateInputValue.trim() });
    setTemplateInputOpen(false);
    setPendingTemplate(null);
    setTemplateInputValue("");
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    // Limit to 5 images per message
    if (attachedImages.length + files.length > 5) {
      toast.error("Maximum 5 images per message");
      return;
    }
    
    setUploadingImage(true);
    
    for (const file of files) {
      try {
        console.log("Uploading image:", file.name, file.size, "bytes");
        const formData = new FormData();
        formData.append("image", file);
        
        const token = getSessionToken();
        // Don't set Content-Type manually - let axios set it with proper boundary
        const response = await axios.post(`${API}/messages/upload-image`, formData, {
          headers: { 
            Authorization: `Bearer ${token}`
          }
        });
        
        console.log("Upload response:", response.data);
        setAttachedImages(prev => [...prev, {
          url: response.data.url,
          filename: response.data.filename,
          preview: URL.createObjectURL(file)
        }]);
      } catch (error) {
        console.error("Upload error:", error.response?.data || error);
        toast.error(error.response?.data?.detail || "Failed to upload image");
      }
    }
    
    setUploadingImage(false);
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const removeAttachedImage = (index) => {
    setAttachedImages(prev => prev.filter((_, i) => i !== index));
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if ((!newMessage.trim() && attachedImages.length === 0) || !selectedConversation) return;

    setSending(true);
    try {
      const token = getSessionToken();
      const payload = {
        customer_id: selectedConversation.customer_id,
        message: newMessage.trim()
      };
      
      // Only include media_urls if there are images attached
      if (attachedImages.length > 0) {
        payload.media_urls = attachedImages.map(img => img.url);
        console.log("Sending MMS with media_urls:", payload.media_urls);
      }
      
      console.log("Sending message payload:", payload);
      const response = await axios.post(`${API}/messages/send`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log("Message sent response:", response.data);
      
      setNewMessage("");
      setAttachedImages([]);
      fetchMessages(selectedConversation.customer_id);
      fetchConversations(); // Update last message in list
    } catch (error) {
      console.error("Send message error:", error.response?.data || error);
      toast.error(error.response?.data?.detail || "Failed to send message");
    } finally {
      setSending(false);
    }
  };

  // Delete conversation handler
  const handleDeleteConversation = async () => {
    if (!conversationToDelete) return;
    
    try {
      const token = getSessionToken();
      await axios.delete(
        `${API}/messages/conversation/${conversationToDelete.customer_id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Conversation deleted");
      setShowDeleteDialog(false);
      setConversationToDelete(null);
      setSelectedConversation(null);
      fetchConversations();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete conversation");
    }
  };

  const confirmDeleteConversation = (conv) => {
    setConversationToDelete(conv);
    setShowDeleteDialog(true);
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return "";
    // Parse as UTC then convert to PST for display
    const date = new Date(dateStr);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) {
      return date.toLocaleTimeString('en-US', { 
        hour: 'numeric', 
        minute: '2-digit', 
        hour12: true,
        timeZone: 'America/Los_Angeles'
      });
    } else if (diffDays === 1) {
      return 'Yesterday';
    } else if (diffDays < 7) {
      return date.toLocaleDateString('en-US', { weekday: 'short', timeZone: 'America/Los_Angeles' });
    } else {
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'America/Los_Angeles' });
    }
  };

  const formatMessageTime = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    
    // Always show time
    const timeStr = date.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit', 
      hour12: true,
      timeZone: 'America/Los_Angeles'
    });
    
    // For today, just show time
    if (diffDays === 0) {
      return timeStr;
    }
    
    // For other days, show day of week + date + time
    const dateStr2 = date.toLocaleDateString('en-US', { 
      weekday: 'short',
      month: 'short', 
      day: 'numeric',
      timeZone: 'America/Los_Angeles'
    });
    
    return `${dateStr2}, ${timeStr}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#207024]"></div>
      </div>
    );
  }

  // Conversation detail view
  if (selectedConversation) {
    const isUnknown = isUnknownCustomer(selectedConversation.customer_name);
    
    return (
      <div className="min-h-screen bg-zinc-50 flex flex-col">
        {/* Header */}
        <div className="bg-white border-b sticky top-0 z-10 pt-[env(safe-area-inset-top)]">
          <div className="max-w-2xl mx-auto px-4 py-3 flex items-center gap-3">
            <button
              onClick={() => setSelectedConversation(null)}
              className="p-2 -ml-2 hover:bg-zinc-100 rounded-lg"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="flex-1">
              {isUnknown ? (
                <>
                  <button
                    onClick={openEditCustomerDialog}
                    className="font-semibold text-amber-600 flex items-center gap-1 hover:underline"
                  >
                    Add Customer Details
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <p className="text-sm text-zinc-500">{selectedConversation.phone}</p>
                </>
              ) : (
                <>
                  <div className="flex items-center gap-2">
                    <h2 className="font-semibold text-zinc-900">{selectedConversation.customer_name}</h2>
                    <button
                      onClick={openEditCustomerDialog}
                      className="p-1 hover:bg-zinc-100 rounded text-zinc-400 hover:text-zinc-600"
                    >
                      <Edit2 className="w-3 h-3" />
                    </button>
                  </div>
                  <p className="text-sm text-zinc-500">{selectedConversation.phone}</p>
                </>
              )}
            </div>
            {/* Estimate button — opens an inline preview panel without
                navigating away. The freshly-fetched `customerEstimate.lead_id`
                is the source of truth (the conversation row's lead_id lags). */}
            {(customerEstimate?.lead_id || selectedConversation.lead_id) && (
              <button
                data-testid="view-estimate-btn"
                onClick={() => setShowEstimatePanel((v) => !v)}
                aria-pressed={showEstimatePanel}
                className={`p-2 rounded-lg ${showEstimatePanel ? "bg-[#207024]/10 text-[#207024]" : "hover:bg-zinc-100 text-[#207024]"}`}
                title={showEstimatePanel ? "Close estimate" : "View estimate"}
              >
                <DollarSign className="w-5 h-5" />
              </button>
            )}
            <button
              onClick={() => confirmDeleteConversation(selectedConversation)}
              className="p-2 hover:bg-red-50 rounded-lg text-red-500"
              title="Delete conversation"
            >
              <Trash2 className="w-5 h-5" />
            </button>
            <a
              href={`tel:${selectedConversation.phone}`}
              className="p-2 hover:bg-zinc-100 rounded-lg text-[#207024]"
            >
              <Phone className="w-5 h-5" />
            </a>
          </div>
        </div>

        {/* Unknown customer banner */}
        {isUnknown && (
          <div className="bg-amber-50 border-b border-amber-200 px-4 py-2">
            <p className="text-sm text-amber-800 text-center">
              New contact - <button onClick={openEditCustomerDialog} className="font-semibold underline">Add their details</button> to save them as a customer
            </p>
          </div>
        )}

        {!isUnknown && selectedConversation.customer_id && !String(selectedConversation.customer_id).startsWith("tech_") && (
          <ReviewReminderBanner key={selectedConversation.customer_id} customerId={selectedConversation.customer_id} />
        )}

        {/* Estimate Panel - Collapsible */}
        {showEstimatePanel && customerEstimate && (
          <InlineEstimatePanel
            estimate={customerEstimate}
            editingLineItems={editingLineItems}
            saving={savingEstimate}
            formatCurrency={formatCurrency}
            onClose={() => setShowEstimatePanel(false)}
            onSave={saveEstimateChanges}
            onUpdateQty={updateLineItemQty}
            onRemoveItem={removeLineItem}
            onOpenFullPage={(leadId) => navigate('/estimates', { state: { viewEstimateId: leadId } })}
          />
        )}

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 pb-32">
          <div className="max-w-2xl mx-auto space-y-3">
            {messages.length === 0 ? (
              <div className="text-center py-12">
                <MessageCircle className="w-12 h-12 text-zinc-300 mx-auto mb-3" />
                <p className="text-zinc-500">No messages yet</p>
                <p className="text-sm text-zinc-400">Send a message to start the conversation</p>
              </div>
            ) : (
              messages.map((msg, idx) => (
                msg.direction === 'system' ? (
                  // System notification - centered, different style
                  <div
                    key={msg.message_id || idx}
                    className="flex justify-center my-2"
                  >
                    <div className="bg-blue-50 border border-blue-200 text-blue-700 px-4 py-2 rounded-full text-sm">
                      {msg.message}
                      <span className="text-blue-400 text-xs ml-2">
                        {formatMessageTime(msg.created_at)}
                      </span>
                    </div>
                  </div>
                ) : (
                <div
                  key={msg.message_id || idx}
                  className={`flex ${msg.direction === 'outbound' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[80%] rounded-2xl px-4 py-2 ${
                      msg.direction === 'outbound'
                        ? 'bg-[#207024] text-white rounded-br-md'
                        : 'bg-white border border-zinc-200 text-zinc-900 rounded-bl-md'
                    }`}
                  >
                    {/* Display attached images */}
                    {msg.media_urls && msg.media_urls.length > 0 && (
                      <div className={`grid gap-2 mb-2 ${msg.media_urls.length > 1 ? 'grid-cols-2' : 'grid-cols-1'}`}>
                        {msg.media_urls.map((url, imgIdx) => (
                          <button 
                            key={imgIdx} 
                            onClick={() => setLightboxImage(url)}
                            className="block cursor-pointer"
                          >
                            <img 
                              src={url} 
                              alt={`Attachment ${imgIdx + 1}`}
                              className="rounded-lg max-w-full h-auto max-h-48 object-cover border border-white/20"
                              onError={(e) => {
                                console.error("Image failed to load:", url);
                                // Show placeholder instead of hiding
                                e.target.src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100' viewBox='0 0 24 24' fill='none' stroke='%23999' stroke-width='2'%3E%3Crect x='3' y='3' width='18' height='18' rx='2'/%3E%3Ccircle cx='8.5' cy='8.5' r='1.5'/%3E%3Cpath d='M21 15l-5-5L5 21'/%3E%3C/svg%3E";
                                e.target.className = "rounded-lg w-24 h-24 bg-zinc-700 p-4";
                              }}
                            />
                          </button>
                        ))}
                      </div>
                    )}
                    {msg.message && <p className="text-sm whitespace-pre-wrap">{msg.message}</p>}
                    <div className={`flex items-center gap-1 mt-1 ${
                      msg.direction === 'outbound' ? 'justify-end' : ''
                    }`}>
                      <span className={`text-xs ${
                        msg.direction === 'outbound' ? 'text-white/70' : 'text-zinc-400'
                      }`}>
                        {formatMessageTime(msg.created_at)}
                      </span>
                      {msg.direction === 'outbound' && (
                        msg.status === 'delivered' ? (
                          <CheckCheck className="w-3 h-3 text-white/70" />
                        ) : msg.status === 'sent' ? (
                          <Check className="w-3 h-3 text-white/70" />
                        ) : msg.status === 'failed' ? (
                          <AlertCircle className="w-3 h-3 text-red-300" />
                        ) : null
                      )}
                    </div>
                  </div>
                </div>
                )
              ))
            )}
            {selectedConversation && <div className="mt-3"><ReplyDraftCard customerId={selectedConversation.customer_id} refreshKey={messages.length} /></div>}
            <div ref={messagesEndRef} />
          </div>
        </div>

        {/* Message Input */}
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t p-4">
          {/* Image Preview Area */}
          {attachedImages.length > 0 && (
            <div className="max-w-2xl mx-auto mb-3">
              <div className="flex gap-2 flex-wrap">
                {attachedImages.map((img, idx) => (
                  <div key={idx} className="relative">
                    <img 
                      src={img.preview} 
                      alt={`Attachment ${idx + 1}`}
                      className="h-16 w-16 object-cover rounded-lg border border-zinc-200"
                    />
                    <button
                      type="button"
                      onClick={() => removeAttachedImage(idx)}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
                {uploadingImage && (
                  <div className="h-16 w-16 rounded-lg border border-zinc-200 flex items-center justify-center bg-zinc-50">
                    <Loader2 className="w-5 h-5 animate-spin text-zinc-400" />
                  </div>
                )}
              </div>
            </div>
          )}
          
          <form onSubmit={(e) => e.preventDefault()} className="max-w-2xl mx-auto flex gap-2 items-end">
            {/* Hidden file input */}
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleImageUpload}
              accept="image/*"
              multiple
              className="hidden"
            />
            
            {/* Template button with dropdown */}
            <div className="relative" ref={templateMenuRef}>
              <button
                type="button"
                onClick={() => setShowTemplateMenu(!showTemplateMenu)}
                className="h-12 w-12 rounded-full border-2 border-zinc-200 hover:bg-zinc-50 text-zinc-500 flex items-center justify-center flex-shrink-0"
                title="Message templates"
              >
                <Plus className="w-5 h-5" />
              </button>
              
              {/* Template dropdown menu */}
              {showTemplateMenu && (
                <div className="absolute bottom-14 left-0 w-64 max-h-[70vh] overflow-y-auto bg-white rounded-xl shadow-lg border border-zinc-200 py-2 z-50" data-testid="composer-plus-menu">
                  {/* New Section */}
                  <div className="px-3 py-2 border-b border-zinc-100">
                    <span className="text-xs font-semibold text-zinc-500 uppercase">Create New</span>
                  </div>
                  <button
                    onClick={() => {
                      setShowTemplateMenu(false);
                      // Navigate to estimates with customer pre-selected for new estimate
                      navigate('/estimates', { 
                        state: { 
                          createEstimate: true,
                          customer: {
                            customer_id: selectedConversation.customer_id,
                            fullName: selectedConversation.customer_name,
                            phoneNumber: selectedConversation.phone
                          }
                        }
                      });
                    }}
                    className="w-full px-3 py-2 text-left hover:bg-zinc-50 transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      <DollarSign className="w-4 h-4 text-emerald-600" />
                      <span className="text-sm font-medium text-zinc-700">New Estimate</span>
                    </div>
                  </button>
                  <button
                    onClick={() => {
                      setShowTemplateMenu(false);
                      setShowJobDialog(true);
                    }}
                    className="w-full px-3 py-2 text-left hover:bg-zinc-50 transition-colors border-b border-zinc-100"
                  >
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-blue-600" />
                      <span className="text-sm font-medium text-zinc-700">New Job</span>
                    </div>
                  </button>
                  
                  {/* How-To Guides — published guides from Settings → Articles & Guides */}
                  <div className="px-3 py-2 border-b border-zinc-100 flex items-center justify-between">
                    <span className="text-xs font-semibold text-zinc-500 uppercase">Guides</span>
                    <button
                      onClick={() => { setShowTemplateMenu(false); navigate("/settings/advertorials"); }}
                      className="text-[10px] font-semibold text-zinc-400 hover:text-[#207024] uppercase tracking-wide"
                    >
                      Manage
                    </button>
                  </div>
                  {guides.length === 0 ? (
                    <div className="px-3 py-3 text-xs text-zinc-400 italic border-b border-zinc-100">
                      No published guides yet.
                    </div>
                  ) : guides.map((g) => (
                    <button
                      key={g.article_id}
                      onClick={() => insertGuideLink(g)}
                      data-testid={`send-guide-${g.slug}`}
                      className="w-full px-3 py-2 text-left hover:bg-zinc-50 transition-colors last-of-type:border-b border-zinc-100"
                    >
                      <div className="flex items-center gap-2">
                        <BookOpen className="w-4 h-4 text-amber-600 shrink-0" />
                        <span className="text-sm font-medium text-zinc-700 truncate">{g.title}</span>
                      </div>
                    </button>
                  ))}

                  {/* Templates Section — admin-managed via Settings → Quick Reply Templates */}
                  <div className="px-3 py-2 border-b border-zinc-100 flex items-center justify-between">
                    <span className="text-xs font-semibold text-zinc-500 uppercase">Templates</span>
                    <button
                      onClick={() => {
                        setShowTemplateMenu(false);
                        navigate("/settings/quick-replies");
                      }}
                      className="text-[10px] font-semibold text-zinc-400 hover:text-[#207024] uppercase tracking-wide"
                    >
                      Manage
                    </button>
                  </div>
                  {dbTemplates.length === 0 ? (
                    <div className="px-3 py-3 text-xs text-zinc-400 italic">
                      No templates yet — tap <strong>Manage</strong> to add one.
                    </div>
                  ) : dbTemplates.map((template) => (
                    <button
                      key={template.template_id}
                      onClick={() => applyTemplate(template)}
                      data-testid={`quickreply-${template.template_id}`}
                      className="w-full px-3 py-2 text-left hover:bg-zinc-50 transition-colors"
                    >
                      <div className="flex items-center gap-2">
                        <FileText className="w-4 h-4 text-[#207024]" />
                        <span className="text-sm font-medium text-zinc-700">{template.label}</span>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
            
            {/* Image upload button */}
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploadingImage || attachedImages.length >= 5}
              className="h-12 w-12 rounded-full border-2 border-zinc-200 hover:bg-zinc-50 text-zinc-500 flex items-center justify-center disabled:opacity-50 flex-shrink-0"
              title="Attach images"
            >
              {uploadingImage ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Image className="w-5 h-5" />
              )}
            </button>
            
            <textarea
              value={newMessage}
              onChange={(e) => {
                setNewMessage(e.target.value);
                // Auto-expand textarea
                e.target.style.height = 'auto';
                e.target.style.height = Math.min(e.target.scrollHeight, 150) + 'px';
              }}
              onKeyDown={(e) => {
                // Explicitly handle Enter to only allow new lines, never form actions
                // This provides extra safety on mobile keyboards that may behave differently
                e.stopPropagation();
              }}
              placeholder={attachedImages.length > 0 ? "Add a caption..." : "Type a message..."}
              className="flex-1 min-h-[48px] max-h-[150px] rounded-2xl border-2 border-zinc-200 px-4 py-3 focus:border-[#207024] focus:outline-none resize-none overflow-y-auto"
              disabled={sending}
              rows={1}
            />
            <button
              type="button"
              onClick={handleSendMessage}
              onKeyDown={(e) => {
                // Prevent Enter key from triggering the button - only allow explicit clicks
                if (e.key === 'Enter') {
                  e.preventDefault();
                  e.stopPropagation();
                }
              }}
              disabled={(!newMessage.trim() && attachedImages.length === 0) || sending}
              className="h-12 w-12 rounded-full bg-[#207024] hover:bg-[#1a5c1d] text-white flex items-center justify-center disabled:opacity-50 flex-shrink-0"
            >
              <Send className="w-5 h-5" />
            </button>
          </form>
        </div>

        {/* Job Dialog for creating new job from conversation */}
        <JobDialog
          open={showJobDialog}
          onOpenChange={setShowJobDialog}
          job={null}
          preselectedCustomer={selectedConversation ? {
            customer_id: selectedConversation.customer_id,
            fullName: selectedConversation.customer_name,
            phoneNumber: selectedConversation.phone
          } : null}
          isAdmin={user?.role === "Admin"}
          onSuccess={() => {
            setShowJobDialog(false);
            toast.success("Job created successfully");
          }}
        />

        {/* Template Input Modal (for ETA, etc.) */}
        <TemplateInputModal
          open={templateInputOpen}
          onOpenChange={setTemplateInputOpen}
          template={pendingTemplate}
          value={templateInputValue}
          onValueChange={setTemplateInputValue}
          onSubmit={handleTemplateInputSubmit}
          onCancel={() => {
            setTemplateInputOpen(false);
            setPendingTemplate(null);
          }}
        />

        {/* Image Lightbox Modal */}
        <ImageLightbox
          imageUrl={lightboxImage}
          onClose={() => setLightboxImage(null)}
        />
      </div>
    );
  }

  // Conversations list view
  return (
    <div className="min-h-screen bg-zinc-50 pb-20">
      <Header user={user} />
      
      <div className="max-w-2xl mx-auto px-4 py-6 overflow-hidden">
        <h1 className="font-barlow text-2xl font-bold text-zinc-900 uppercase mb-4">
          Messages
        </h1>

        {user?.role === "Admin" && <AIDraftsBanner />}

        {/* Search */}
        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search conversations..."
            className="w-full h-12 rounded-xl border-2 border-zinc-200 pl-10 pr-4 focus:border-[#207024] focus:outline-none"
          />
        </div>

        {/* Status Filter Tabs */}
        <div className="flex gap-1 mb-4 overflow-x-auto pb-1">
          {[
            { id: "nurturing", label: "Nurturing", color: "bg-purple-100 text-purple-700" },
            { id: "scheduled", label: "Scheduled", color: "bg-blue-100 text-blue-700" },
            { id: "converted", label: "Converted", color: "bg-green-100 text-green-700" },
            { id: "lost", label: "Lost", color: "bg-red-100 text-red-700" },
            { id: "techs", label: "Techs", color: "bg-orange-100 text-orange-700" },
            { id: "quotes", label: "Sent Quotes", color: "bg-amber-100 text-amber-700" }
          ].map(tab => {
            const counts = getStatusCounts();
            const count = counts[tab.id];
            const isActive = statusFilter === tab.id;
            return (
              <button
                key={tab.id}
                data-testid={`filter-tab-${tab.id}`}
                onClick={() => {
                  setStatusFilter(tab.id);
                  if (tab.id === "quotes" && sentQuotes.length === 0) {
                    fetchSentQuotes();
                  }
                }}
                className={`px-3 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                  isActive 
                    ? tab.color + " ring-2 ring-offset-1 ring-zinc-400"
                    : "bg-zinc-50 text-zinc-500 hover:bg-zinc-100"
                }`}
              >
                {tab.label}
                {count > 0 && (
                  <span className={`ml-1.5 text-xs ${isActive ? "" : "text-zinc-400"}`}>
                    ({count})
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Conversations List or Sent Quotes */}
        {statusFilter === "quotes" ? (
          <div className="space-y-2" data-testid="sent-quotes-list">
            {loadingQuotes ? (
              <div className="text-center py-12">
                <Loader2 className="w-8 h-8 text-zinc-400 mx-auto animate-spin" />
              </div>
            ) : sentQuotes.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-xl border">
                <Send className="w-12 h-12 text-zinc-300 mx-auto mb-3" />
                <p className="text-zinc-500">No quote links sent yet</p>
                <p className="text-sm text-zinc-400 mt-1">
                  Rosie auto-quote texts will appear here once enabled
                </p>
              </div>
            ) : (
              sentQuotes.map((quote, idx) => {
                const isRosie = quote.utm_source === "rosie_call" || quote.sent_by === "Rosie Auto-Quote";
                return (
                <div key={quote.message_id || idx} className="bg-white rounded-xl border p-4">
                  <div className="flex items-start gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                      isRosie ? "bg-orange-100" : "bg-amber-100"
                    }`}>
                      {isRosie ? (
                        <Phone className="w-5 h-5 text-orange-600" />
                      ) : (
                        <Send className="w-5 h-5 text-amber-600" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="font-semibold text-zinc-900 truncate">
                            {quote.rosie_caller_name || quote.customer_name?.replace("Quick Quote - ", "") || quote.phone}
                          </span>
                          {isRosie && (
                            <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-orange-100 text-orange-700 flex-shrink-0">
                              Rosie
                            </span>
                          )}
                        </div>
                        <span className="text-xs text-zinc-400 flex-shrink-0">
                          {formatTime(quote.sent_at || quote.created_at)}
                        </span>
                      </div>
                      <p className="text-sm text-zinc-500">{quote.phone}</p>
                      <p className="text-sm text-zinc-600 mt-1 line-clamp-2">{quote.body || quote.message}</p>
                      <div className="flex items-center gap-2 mt-1.5">
                        <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${
                          quote.status === "sent" || quote.status === "delivered" 
                            ? "bg-green-100 text-green-700" 
                            : "bg-zinc-100 text-zinc-500"
                        }`}>
                          {quote.status || "sent"}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                );
              })
            )}
          </div>
        ) : (
        <div className="space-y-2">
          {filteredConversations.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-xl border">
              <MessageCircle className="w-12 h-12 text-zinc-300 mx-auto mb-3" />
              <p className="text-zinc-500">
                {`No ${statusFilter} conversations`}
              </p>
              <p className="text-sm text-zinc-400 mt-1">
                Messages from customers will appear here
              </p>
            </div>
          ) : (
            filteredConversations.map((conv) => {
              const isUnknown = isUnknownCustomer(conv.customer_name);
              const hasUnread = conv.unread_count > 0;
              const statusColors = {
                nurturing: "bg-purple-100 text-purple-700",
                scheduled: "bg-blue-100 text-blue-700",
                converted: "bg-green-100 text-green-700",
                lost: "bg-red-100 text-red-700"
              };
              return (
                <div
                  key={conv.customer_id || conv.phone}
                  data-testid={`conversation-${conv.phone}`}
                  className={`rounded-xl border p-4 hover:border-[#207024]/30 transition-colors overflow-hidden ${
                    hasUnread 
                      ? "bg-green-50 border-[#207024]/40 border-l-4 border-l-[#207024]" 
                      : "bg-white"
                  } ${isUnknown ? "border-amber-200" : ""}`}
                >
                  <div className="flex items-start gap-3 overflow-hidden">
                    <button
                      onClick={() => setSelectedConversation(conv)}
                      className="flex items-start gap-3 flex-1 text-left min-w-0 overflow-hidden"
                    >
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 ${
                        hasUnread ? "bg-[#207024]/10" : isUnknown ? "bg-amber-100" : "bg-zinc-100"
                      }`}>
                        <User className={`w-6 h-6 ${hasUnread ? "text-[#207024]" : isUnknown ? "text-amber-500" : "text-zinc-400"}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex items-center gap-2 min-w-0">
                            <h3 className={`truncate ${
                              hasUnread ? "font-bold text-zinc-900" : "font-semibold"
                            } ${isUnknown ? "text-amber-600" : "text-zinc-900"}`}>
                              {isUnknown ? "New Contact" : conv.customer_name}
                            </h3>
                            {conv.conversation_status && (
                              <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${statusColors[conv.conversation_status] || "bg-zinc-100 text-zinc-500"}`}>
                                {conv.conversation_status}
                              </span>
                            )}
                          </div>
                          <span className="text-xs text-zinc-400 flex-shrink-0">
                            {formatTime(conv.last_message_at)}
                          </span>
                        </div>
                        <p className="text-sm text-zinc-500 truncate">{conv.phone}</p>
                        {conv.has_messages === false ? (
                          <p className="text-sm truncate mt-1 text-zinc-400 italic">
                            No messages yet
                          </p>
                        ) : (
                          <p className={`text-sm truncate mt-1 ${hasUnread ? "text-zinc-900 font-medium" : "text-zinc-600"}`}>
                            {conv.last_message || "No messages"}
                          </p>
                        )}
                      </div>
                    </button>
                    {hasUnread && (
                      <div className="flex-shrink-0 mt-1">
                        <span data-testid="unread-badge" className="bg-[#207024] text-white text-xs rounded-full min-w-[22px] h-[22px] flex items-center justify-center font-bold px-1.5">
                          {conv.unread_count}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
        )}
      </div>

      {/* New Message Dialog */}
      <Dialog open={showNewMessageDialog} onOpenChange={setShowNewMessageDialog}>
        <DialogContent className="max-w-md max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>New Message</DialogTitle>
          </DialogHeader>
          
          {/* Customer Search */}
          <div className="relative mb-4">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-400" />
            <input
              type="text"
              value={customerSearch}
              onChange={(e) => setCustomerSearch(e.target.value)}
              placeholder="Search customers..."
              className="w-full h-12 rounded-xl border-2 border-zinc-200 pl-10 pr-4 focus:border-[#207024] focus:outline-none"
            />
          </div>
          
          {/* Customer List */}
          <div className="flex-1 overflow-y-auto space-y-2">
            {customers
              .filter(c => 
                c.fullName?.toLowerCase().includes(customerSearch.toLowerCase()) ||
                c.phoneNumber?.includes(customerSearch)
              )
              .filter(c => c.phoneNumber) // Only show customers with phone numbers
              .map((customer) => (
                <button
                  key={customer.customer_id}
                  onClick={() => startNewConversation(customer)}
                  className="w-full bg-zinc-50 rounded-xl p-4 text-left hover:bg-zinc-100 transition-colors flex items-center gap-3"
                >
                  <div className="w-10 h-10 rounded-full bg-zinc-200 flex items-center justify-center flex-shrink-0">
                    <User className="w-5 h-5 text-zinc-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-zinc-900 truncate">{customer.fullName}</h3>
                    <p className="text-sm text-zinc-500 truncate">{customer.phoneNumber}</p>
                  </div>
                </button>
              ))}
            
            {customers.filter(c => c.phoneNumber).length === 0 && (
              <div className="text-center py-8">
                <User className="w-10 h-10 text-zinc-300 mx-auto mb-2" />
                <p className="text-zinc-500">No customers with phone numbers</p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Customer Dialog */}
      <Dialog open={showEditCustomerDialog} onOpenChange={setShowEditCustomerDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {isUnknownCustomer(selectedConversation?.customer_name) 
                ? "Add Customer Details" 
                : "Edit Customer Name"}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-zinc-700 mb-1">Customer Name</label>
              <input
                type="text"
                value={editingCustomerName}
                onChange={(e) => setEditingCustomerName(e.target.value)}
                placeholder="Enter customer name"
                className="w-full h-12 rounded-xl border-2 border-zinc-200 px-4 focus:border-[#207024] focus:outline-none"
                autoFocus
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-zinc-700 mb-1">Phone Number</label>
              <input
                type="text"
                value={selectedConversation?.phone || ""}
                disabled
                className="w-full h-12 rounded-xl border-2 border-zinc-100 px-4 bg-zinc-50 text-zinc-500"
              />
            </div>

            <div className="flex gap-3 pt-2">
              <button
                onClick={() => setShowEditCustomerDialog(false)}
                className="flex-1 h-12 rounded-xl border-2 border-zinc-200 text-zinc-700 font-semibold hover:bg-zinc-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveCustomerName}
                disabled={savingCustomer || !editingCustomerName.trim()}
                className="flex-1 h-12 rounded-xl bg-[#207024] hover:bg-[#1a5c1d] text-white font-semibold disabled:opacity-50"
              >
                {savingCustomer ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Conversation Confirmation */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent className="border-2 border-red-200">
          <AlertDialogHeader>
            <div className="flex items-center gap-3 text-red-600 mb-2">
              <Trash2 className="w-6 h-6" />
              <AlertDialogTitle className="text-red-700 text-xl">Delete Conversation?</AlertDialogTitle>
            </div>
            <AlertDialogDescription className="text-base">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-3">
                <p className="font-semibold text-red-800 mb-2">This action cannot be undone!</p>
                <p className="text-red-700">
                  You are about to permanently delete all messages with{" "}
                  <span className="font-bold">{conversationToDelete?.customer_name}</span>
                  {conversationToDelete?.phone && (
                    <span> ({conversationToDelete.phone})</span>
                  )}
                </p>
              </div>
              <p className="text-zinc-600">
                This will remove all message history from both the portal and reports.
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-3">
            <AlertDialogCancel onClick={() => setConversationToDelete(null)} className="flex-1">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeleteConversation}
              className="bg-red-600 hover:bg-red-700 flex-1"
            >
              Yes, Delete Conversation
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <BottomNav />
    </div>
  );
}
